import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { sortData, getInfo } from '../../utils/api';
import { toast } from 'sonner';
import { useT } from '../../i18n';

const SortOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [sortColumns, setSortColumns] = useState([{ column: '', order: 'asc' }]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadColumns = async () => {
      try {
        const result = await getInfo();
        setColumns(result.info.column_names || []);
        if (result.info.column_names.length > 0) {
          setSortColumns([{ column: result.info.column_names[0], order: 'asc' }]);
        }
      } catch (error) {
        toast.error(t('form.loadColumnsFailed'));
      }
    };
    loadColumns();
  }, []);

  const addSortColumn = () => {
    setSortColumns([...sortColumns, { column: columns[0] || '', order: 'asc' }]);
  };

  const removeSortColumn = (index) => {
    setSortColumns(sortColumns.filter((_, i) => i !== index));
  };

  const updateSortColumn = (index, field, value) => {
    const updated = [...sortColumns];
    updated[index][field] = value;
    setSortColumns(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await sortData(sortColumns);
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('sort.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-3">
        {sortColumns.map((sc, idx) => (
          <div key={idx} className="card p-4">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="label mb-1">{t('form.column')}</label>
                <select
                  value={sc.column}
                  onChange={(e) => updateSortColumn(idx, 'column', e.target.value)}
                  className="select"
                  required
                >
                  {columns.map(col => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="label mb-1">{t('sort.order')}</label>
                <select
                  value={sc.order}
                  onChange={(e) => updateSortColumn(idx, 'order', e.target.value)}
                  className="select"
                  required
                >
                  <option value="asc">{t('sort.ascending')}</option>
                  <option value="desc">{t('sort.descending')}</option>
                </select>
              </div>
              {sortColumns.length > 1 && (
                <button type="button" onClick={() => removeSortColumn(idx)} className="btn-ghost h-8 w-8 p-0 mt-6">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={addSortColumn} className="btn-secondary flex items-center gap-2">
        <Plus className="h-4 w-4" />
        {t('sort.addColumn')}
      </button>
      <div className="flex gap-2 pt-4 border-t">
        <button type="submit" disabled={loading} className="btn-primary flex-1" data-testid="apply-sort">
          {loading ? t('form.applying') : t('sort.apply')}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          {t('form.cancel')}
        </button>
      </div>
    </form>
  );
};

export default SortOperation;